<template>
    <div class="flex cursor-pointer justify-between space-x-4" @click="markAsRead">
        <div class="shrink-0">
            <Avatar :src="fromUser.avatar" :alt="fromUser.tag_name" />
        </div>
        <div class="flex-1">
            <div class="flex items-start justify-between">
                <div class="flex-1">
                    <p class="mt-1 text-sm">
                        <strong>{{ fromUser.tag_name }}</strong> {{ notificationMessage }}
                    </p>
                </div>
                <span class="text-titles ml-4 text-sm font-bold">
                    {{ formattedDate }}
                </span>
            </div>

            <div
                class="mt-4 grid gap-4"
                :class="isActionable && !isBlocked ? 'grid-cols-3' : 'grid-cols-2'"
            >
                <template v-if="isBlocked">
                    <DiagonalCutButton
                        variant="secondary"
                        cut-position="both-top"
                        class="col-span-2"
                        :loading="isUnblocking"
                        :disabled="isUnblocking"
                        data-testid="friend-unblock-button"
                        @click.stop="unblock"
                    >
                        {{ $t('notifications.unblock') }}
                    </DiagonalCutButton>
                </template>

                <template v-else-if="isActionable">
                    <DiagonalCutButton
                        variant="primary"
                        cut-position="right-top"
                        :loading="isAccepting"
                        :disabled="isAccepting || isDeclining || isBlocking"
                        @click.stop="accept"
                    >
                        {{ $t('notifications.accept') }}
                    </DiagonalCutButton>
                    <DiagonalCutButton
                        variant="secondary"
                        cut-position="both-top"
                        :loading="isDeclining"
                        :disabled="isAccepting || isDeclining || isBlocking"
                        @click.stop="decline"
                    >
                        {{ $t('notifications.decline') }}
                    </DiagonalCutButton>
                    <DiagonalCutButton
                        variant="secondary"
                        cut-position="left-top"
                        :loading="isBlocking"
                        :disabled="isAccepting || isDeclining || isBlocking"
                        @click.stop="block"
                    >
                        {{ $t('notifications.block') }}
                    </DiagonalCutButton>
                </template>

                <template v-else-if="notification.status === 'accepted'">
                    <DiagonalCutButton
                        variant="primary"
                        cut-position="right-top"
                        class="col-span-2 cursor-default opacity-60"
                        disabled
                    >
                        <span class="flex items-center justify-center gap-2">
                            <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fill-rule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                            {{ $t('notifications.accepted') }} {{ actionTime }}
                        </span>
                    </DiagonalCutButton>
                </template>

                <template v-else-if="notification.status === 'declined'">
                    <DiagonalCutButton
                        variant="secondary"
                        cut-position="right-top"
                        class="col-span-2 cursor-default opacity-50"
                        disabled
                    >
                        {{ $t('notifications.declined') }} {{ actionTime }}
                    </DiagonalCutButton>
                </template>

                <template v-else-if="isRequestAccepted">
                    <DiagonalCutButton
                        variant="primary"
                        cut-position="right-top"
                        class="col-span-2 cursor-default opacity-60"
                        disabled
                    >
                        <span class="flex items-center justify-center gap-2">
                            <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fill-rule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                            {{ $t('notifications.friend_request_accepted') }}
                        </span>
                    </DiagonalCutButton>
                </template>

                <template v-else-if="isRequestDeclined">
                    <DiagonalCutButton
                        variant="secondary"
                        cut-position="right-top"
                        class="col-span-2 cursor-default opacity-50"
                        disabled
                    >
                        {{ $t('notifications.friend_request_declined') }}
                    </DiagonalCutButton>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import { useAuthStore } from '@/stores/auth.js'
import DiagonalCutButton from '@/components/ui/DiagonalCutButton.vue'
import Avatar from '@/components/Avatar.vue'
import { formatRelativeTime } from '@/utils/dateHelpers.js'

export default {
    name: 'FriendshipNotification',

    components: {
        DiagonalCutButton,
        Avatar,
    },

    props: {
        notification: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isAccepting: false,
            isDeclining: false,
            isBlocking: false,
            isUnblocking: false,
        }
    },

    mounted() {
        const authStore = useAuthStore()
        if (!authStore.blockedUsersLoaded) {
            authStore.fetchBlockedUsers().catch((error) => {
                console.error('Failed to preload blocked users:', error)
            })
        }
    },

    computed: {
        fromUser() {
            return this.notification?.data?.from_user || {}
        },

        notificationMessage() {
            const typeMap = {
                'App\\Notifications\\FriendRequestReceivedNotification':
                    'notifications.friend_would_like',
                'App\\Notifications\\FriendRequestAcceptedNotification':
                    'notifications.friend_accepted_request',
                'App\\Notifications\\FriendRequestDeclinedNotification':
                    'notifications.friend_declined_request',
                'App\\Notifications\\FriendshipRemovedNotification':
                    'notifications.friend_removed',
                'App\\Notifications\\FriendshipCancelledRequestNotification':
                    'notifications.friend_cancelled_request',
                'App\\Notifications\\FriendRequestSentNotification':
                    'notifications.friend_request_sent',
                'App\\Notifications\\FriendshipEstablishedNotification':
                    'notifications.now_friends',
            }
            return this.$t(typeMap[this.notification.type] || 'notifications.friend_would_like')
        },

        formattedDate() {
            return formatRelativeTime(this.notification.created_at)
        },

        isRead() {
            return !!this.notification.read_at
        },

        isRequestReceived() {
            return (
                this.notification.type === 'App\\Notifications\\FriendRequestReceivedNotification'
            )
        },

        isRequestAccepted() {
            return (
                this.notification.type ===
                    'App\\Notifications\\FriendRequestAcceptedNotification' ||
                this.notification.type === 'App\\Notifications\\FriendshipEstablishedNotification'
            )
        },

        isRequestDeclined() {
            return (
                this.notification.type === 'App\\Notifications\\FriendRequestDeclinedNotification'
            )
        },

        isActionable() {
            return this.isRequestReceived && this.notification.status === 'pending'
        },

        isBlocked() {
            if (!this.isRequestReceived) return false
            const profileId = this.fromUser?.id
            if (!profileId) return false

            const authStore = useAuthStore()
            const isUserBlocked = authStore.blocked_users.some(
                (b) => b.blocked_profile_id === profileId,
            )
            if (!isUserBlocked) return false

            const latestRequestId = authStore.inbox_notifications
                .filter(
                    (n) =>
                        n.type ===
                            'App\\Notifications\\FriendRequestReceivedNotification' &&
                        n.data?.from_user?.id === profileId,
                )
                .reduce(
                    (latest, n) =>
                        !latest || new Date(n.created_at) > new Date(latest.created_at)
                            ? n
                            : latest,
                    null,
                )?.id

            return latestRequestId === this.notification.id
        },

    },

    methods: {
        markAsRead() {
            this.$emit('read', this.notification)
        },

        async accept() {
            if (this.isAccepting || this.isDeclining) return

            this.isAccepting = true
            try {
                const friendshipId = this.notification.data?.friendship?.id
                if (!friendshipId) {
                    throw new Error('No friendship ID found in notification')
                }
                await useAuthStore().acceptFriendRequest(friendshipId)
            } catch (error) {
                console.error('Failed to accept friend request:', error)
                this.$toast(
                    this.$t('general.something_went_wrong'),
                    5000,
                    this.$t('friends.error_accept_request'),
                )
            } finally {
                this.isAccepting = false
            }
        },

        async decline() {
            if (this.isAccepting || this.isDeclining) return

            this.isDeclining = true
            try {
                const friendshipId = this.notification.data?.friendship?.id
                if (!friendshipId) {
                    throw new Error('No friendship ID found in notification')
                }
                await useAuthStore().declineFriendRequest(friendshipId)
                this.$emit('read', this.notification)
            } catch (error) {
                console.error('Failed to decline friend request:', error)
                this.$toast(
                    this.$t('general.something_went_wrong'),
                    5000,
                    this.$t('friends.error_decline_request'),
                )
            } finally {
                this.isDeclining = false
            }
        },

        async unblock() {
            if (this.isUnblocking) return

            const profileId = this.fromUser?.id
            if (!profileId) return

            this.isUnblocking = true
            try {
                await useAuthStore().unblockProfile(profileId)
                this.$toast(
                    this.$t('friends.user_unblocked_title'),
                    3000,
                    this.$t('friends.user_unblocked_body', {
                        name: this.fromUser.tag_name || this.$t('friends.this_user'),
                    }),
                )
            } catch (error) {
                console.error('Failed to unblock user:', error)
                this.$toast(
                    this.$t('general.something_went_wrong'),
                    5000,
                    error.data?.message || this.$t('friends.error_unblock_user'),
                )
            } finally {
                this.isUnblocking = false
            }
        },

        async block() {
            if (this.isAccepting || this.isDeclining || this.isBlocking) return

            const profileId = this.fromUser?.id
            if (!profileId) {
                this.$toast(
                    this.$t('general.something_went_wrong'),
                    5000,
                    this.$t('friends.error_identify_block'),
                )
                return
            }

            this.isBlocking = true
            try {
                const authStore = useAuthStore()
                await authStore.blockProfile(profileId)

                const friendshipId = this.notification.data?.friendship?.id
                if (friendshipId) {
                    await authStore.declineFriendRequest(friendshipId)
                }

                this.$emit('read', this.notification)
                this.$toast(
                    this.$t('friends.user_blocked_title'),
                    3000,
                    this.$t('friends.user_blocked_body', {
                        name: this.fromUser.tag_name || this.$t('friends.this_user'),
                    }),
                )
            } catch (error) {
                console.error('Failed to block user:', error)
                this.$toast(
                    this.$t('general.something_went_wrong'),
                    5000,
                    error.data?.message || this.$t('friends.error_block_user'),
                )
            } finally {
                this.isBlocking = false
            }
        },
    },
}
</script>
